import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Grid, Typography } from '@mui/material';

import AppEntityAvatar, {
  AvatarSize,
} from '../../../components/designe/app-entity-avatar/AppEntityAvatar';
import AppFileContainer from '../../../components/designe/app-file-container/AppFileContainer';
import AppTextField from '../../../components/designe/app-text-field/AppTextField';
import ConfirmDialog from '../../../components/reusable/confirm-dialog/ConfirmDialog';
import withRouter from '../../../hooks/with-router';
import theme from '../../../styles/theme';
import { useStyles } from './Exercise.styles';
import { useExercisePage } from './useExercisePage';
import AppButton from '../../../components/designe/app-button/AppButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIos';
import { useNavigate } from 'react-router-dom';
import { useExercise } from '../useExercise';
import { useGetExerciseLoadFormulas } from '../../../services/redux/api/appConfigApi';
import { ExerciseScope } from '../../../shared/constants';
import { IExerciseProperty } from '../../../types/interfaces/exercise-property';

function formatPropertyList(items?: IExerciseProperty[]): string {
  if (!items?.length) return '—';
  const names = items.map((i) => i.name).filter(Boolean);
  return names.length ? names.join(', ') : '—';
}

function formatBool(value: boolean | undefined) {
  if (value === undefined || value === null) return '—';
  return value ? '\u2713' : '—';
}

const Exercise = (props) => {
  const { t } = useTranslation();
  const { classes } = useStyles(theme);
  const exerciseId = props.exerciseId ?? props.params?.exerciseId;
  const {
    exercise,
    languages,
    onEditClick,
    onDelete,
    getOptionsLabel,
    exerciseLanguage,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
  } = useExercisePage(exerciseId, {
    onBack: props.onBack,
    onEditClick: props.onEditClick,
  });
  const navigate = useNavigate();
  const handleBack = props.onBack ?? (() => navigate(-1));
  const { renderExerciseActionButton } = useExercise();
  const { data: formulas } = useGetExerciseLoadFormulas();

  const exerciseFormula = useMemo(() => {
    if (!exercise?.exerciseLoadFormulaId || !formulas?.length) return null;
    return formulas.find((f) => f.id === exercise.exerciseLoadFormulaId);
  }, [exercise?.exerciseLoadFormulaId, formulas]);

  const scopeLabel =
    exercise?.scope === ExerciseScope.public
      ? t('exercises.scope_public')
      : exercise?.scope === ExerciseScope.private
        ? t('exercises.scope_private')
        : (exercise?.scope ?? '—');

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" marginBottom={2}>
        <AppButton variant="text" onClick={handleBack}>
          <ArrowBackIcon />
          <Typography variant="h2">{t('common.exercise')}</Typography>
        </AppButton>
        {exercise &&
          renderExerciseActionButton(exercise, onEditClick, () =>
            setShowDeleteConfirmation(true),
          )}
      </Box>

      {exercise && (
        <Box className={classes.exerciseWrapper}>
          <Box className={classes.detailBlock}>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t('exercises.photos')}
            </Typography>
            <Box className={classes.itemWrapper}>
              <AppFileContainer
                files={exercise.files}
                mainFileId={exercise.avatarFileId ?? exercise.avatar?.id}
              />
            </Box>
          </Box>

          {exercise.exerciseTranslations &&
            exercise.exerciseTranslations.length > 0 && (
              <Box className={classes.detailBlock}>
                <Typography variant="h3" sx={{ mb: 2 }}>
                  {t('exercises.translations')}
                </Typography>
                {exercise.exerciseTranslations.map((translation, index) => (
                  <Box key={translation.languageId} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      {languages.find((l) => l.id === translation.languageId)
                        ?.title ?? ''}
                    </Typography>
                    <AppTextField
                      label={t('common.title')}
                      value={exercise.exerciseTranslations[index]?.title || '—'}
                      viewMode
                    />
                    <AppTextField
                      label={t('common.description')}
                      value={
                        exercise.exerciseTranslations[index]?.description || '—'
                      }
                      viewMode
                    />
                  </Box>
                ))}
              </Box>
            )}

          <Box className={classes.detailBlock}>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t('exercises.detailsMusclesAndEquipment')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <AppTextField
                  label={t('exercises.mainMuscleGroup')}
                  value={exercise.mainMuscleGroup?.name ?? '—'}
                  viewMode
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <AppTextField
                  label={t('exercises.equipment')}
                  value={exercise.equipment?.name ?? '—'}
                  viewMode
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <AppTextField
                  label={t('exercises.additionalMuscles')}
                  value={formatPropertyList(exercise.additionalMuscles)}
                  viewMode
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <AppTextField
                  label={t('exercises.additionalEquipments')}
                  value={formatPropertyList(exercise.additionalEquipments)}
                  viewMode
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <AppTextField
                  label={t('exercises.developedSkills')}
                  value={formatPropertyList(exercise.developedSkills)}
                  viewMode
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <AppTextField
                  label={t('exercises.category')}
                  value={exercise.category?.name ?? '—'}
                  viewMode
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <AppTextField
                  label={t('exercises.language')}
                  value={exerciseLanguage ?? '—'}
                  viewMode
                />
              </Grid>
            </Grid>
          </Box>

          <Box className={classes.detailBlock}>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t('exercises.detailsLoadAndAccess')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <AppTextField
                  label={t('exercises.formulaForCalculationLoad')}
                  value={
                    exerciseFormula?.name ||
                    exerciseFormula?.nameKey ||
                    exercise.exerciseLoadFormula?.name ||
                    exercise.exerciseLoadFormula?.nameKey ||
                    '—'
                  }
                  viewMode
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <AppTextField
                  label={t('exercises.scope')}
                  value={scopeLabel}
                  viewMode
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <AppTextField
                  label={t('exercises.canCopy')}
                  value={formatBool(exercise.canCopy)}
                  viewMode
                />
              </Grid>
            </Grid>
          </Box>

          {exercise.owner && (
            <Box className={classes.detailBlock}>
              <Typography variant="h3" sx={{ mb: 2 }}>
                {t('common.owner')}
              </Typography>
              <Box className={classes.ownerWrapper}>
                <AppEntityAvatar entity={exercise.owner} size={AvatarSize.s} />
                <Typography variant="text-md">
                  {exercise.owner.firstname} {exercise.owner.lastname || ''}
                </Typography>
              </Box>
            </Box>
          )}

          <Box className={classes.detailBlock}>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t('exercises.options')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <AppTextField
                  label={t('exercises.splitBySide')}
                  value={formatBool(exercise.splitBySide)}
                  viewMode
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <AppTextField
                  label={t('exercises.selectedOptions')}
                  value={getOptionsLabel() || '—'}
                  viewMode
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}

      <ConfirmDialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        title={t('common.delete')}
        onSubmit={() => {
          onDelete();
        }}>
        {t('common.deleteQuestion')}
      </ConfirmDialog>
    </Box>
  );
};

export default withRouter(Exercise);
